// Format the countdown time
import notification from '@utils/notification';

export const updateCountdown = (setTimeLeft) => {
    const formatTime = (time) => String(time).padStart(2, '0');

    const now = new Date();
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);

    const timeDifference = endOfDay - now;
    if (timeDifference <= 0) {
        setTimeLeft('dailyMission.00:00:00');
        return;
    }

    const hours = formatTime(Math.floor((timeDifference / (1000 * 60 * 60)) % 24));
    const minutes = formatTime(Math.floor((timeDifference / (1000 * 60)) % 60));
    const seconds = formatTime(Math.floor((timeDifference / 1000) % 60));

    setTimeLeft(`${hours}:${minutes}:${seconds}`);
};

// Map mission statuses to text
export const getStatusText = (status) => {
    switch (status) {
        case 'C':
            return 'dailyMission.claimed';
        case 'A':
            return 'dailyMission.claim';
        case 'P':
            return 'dailyMission.pending';
        default:
            return '';
    }
};

// Map mission names to display names
export const getMissionName = (t, missionName) => {
    return t(`dailyMission.missionType.${missionName}`) || missionName;
};

// Map rewards to text
export const getRewardText = (t, rewardType, reward, currency) => {
    return t(`dailyMission.rewardType.${rewardType}`, { reward, currency });
};

// Map rewards to text
export const getMissionType = (t, targetType, target, currency) => {
    if (targetType !== 'TIME_RANGE') {
        return t(`dailyMission.targetType.${targetType}`, { target, currency }) || targetType;
    } else {
        return t(`dailyMission.missionType.DAILY_LOGIN`);
    }
};

// Fetch daily missions
export const fetchDailyMissions = async (setMissions, setIsLoading) => {
    setIsLoading(true);
    try {
        const response = await window.SPL_Member.getAllDailyMission();

        const sortedMissions = (response || []).sort((a, b) => {
            const getPriority = (status) => {
                if (status === 'A') return 0;
                if (status === 'C') return 2;
                return 1;
            };

            return getPriority(a.status) - getPriority(b.status);
        });

        setMissions(sortedMissions);
    } catch (error) {
        console.error('Failed to fetch daily missions', error);
        notification.showNotification('error', 'Unable to fetch daily missions');
    } finally {
        setIsLoading(false);
    }
};

// Fetch expiring points
export const checkMemberTotalExpiringPoint = async (setExpiringPoints, setExpiringDate) => {
    const endOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999);

    const date = `${endOfMonth.getFullYear()}-${String(endOfMonth.getMonth() + 1).padStart(2, '0')}-${String(endOfMonth.getDate()).padStart(2, '0')}T23:59:59.999Z`;

    try {
        const data = await window.SPL_Member.getMemberTotalExpiringPoint(date);

        if (!data || !data.data) {
            console.warn('No expiring points data found');
            setExpiringPoints(0);
            setExpiringDate('-'); // Set a default or placeholder
            return;
        }

        // Set expiring points
        setExpiringPoints(data.data.expiringPoint || 0);

        // Ensure expiryTime exists before formatting
        if (data.data.expiryTime) {
            const expiryTime = new Date(data.data.expiryTime);
            const formattedDate = `${expiryTime.getFullYear()}-${String(expiryTime.getMonth() + 1).padStart(2, '0')}-${String(expiryTime.getDate()).padStart(2, '0')}`;
            setExpiringDate(formattedDate);
        } else {
            setExpiringDate('-'); // Default if no expiry time
        }
    } catch (error) {
        console.error('Failed to fetch expiring points', error);
        setExpiringPoints(0);
        setExpiringDate('-'); // Handle errors gracefully
    }
};

// Handle claim for a single mission
export const handleClaimClick = async (missionId, points, setRewardPoints, setIsLoading, fetchDailyMissions, getMemberMissionPoints, setIsClaimModalOpen) => {
    setRewardPoints(points);
    setIsLoading(true);

    try {
        const res = await window.SPL_Member.postMissionClaim(missionId);
        if (res && res.code === '10000') {
            fetchDailyMissions(); // Refresh mission data
            getMemberMissionPoints(); // Refresh member points
            setIsClaimModalOpen(true); // Open claim modal
        } else {
            notification.showNotification('error', res.message || 'Failed to claim mission');
        }
    } catch (error) {
        console.error('Error claiming mission:', error);
        notification.showNotification('error', 'An error occurred while claiming the mission');
    } finally {
        setIsLoading(false); // End loading state
    }
};

// Close the claim modal
export const closeClaimModal = (setIsClaimModalOpen) => {
    setIsClaimModalOpen(false);
};

// Handle claiming all missions
export const claimAll = async (setIsLoading, fetchDailyMissions, getMemberMissionPoints) => {
    setIsLoading(true);

    try {
        const res = await window.SPL_Member.postMissionClaimAll();
        if (res && res.code === '10000') {
            fetchDailyMissions(); // Refresh mission data
            getMemberMissionPoints(); // Refresh member points
        } else {
            notification.showNotification('error', res.message || 'Failed to claim all missions');
        }
    } catch (error) {
        console.error('Error claiming all missions:', error);
        notification.showNotification('error', 'An error occurred while claiming all missions');
    } finally {
        setIsLoading(false); // End loading state
    }
};
